// competition-results.js — Tab 3: Competitive sheet viewer with gap + book-status badges.
// Gap classes: ANYONES_GAME (0-3 pts, gray) / CLOSE (4-6 pts, amber) / NOT_IN_BALLPARK (7+, red).
// Book status: ROOM_TO_GROW / MAXED_OUT / OVERPLAYED — from sub1 vs sub2 diff.

import { apiFetch } from './utils.js';

// Gap threshold — operates on raw score points
function classifyGap(rawPoints) {
  const abs = Math.abs(Number(rawPoints));
  if (abs <= 3) return 'ANYONES_GAME';
  if (abs <= 6) return 'CLOSE';
  return 'NOT_IN_BALLPARK';
}

// Book status — sub1=Composition/Vocab (left box), sub2=Achievement/Execution (right box)
function computeBookStatus(sub1, sub2) {
  if (sub1 == null || sub2 == null) return null;
  const diff = Number(sub1) - Number(sub2);
  if (diff >= 2)  return 'ROOM_TO_GROW';
  if (diff <= -2) return 'OVERPLAYED';
  return 'MAXED_OUT';
}

// Expose on window so other modules can call these
window.classifyGap     = classifyGap;
window.computeBookStatus = computeBookStatus;

window.fmtBookStatus = function(sub1, sub2) {
  const s = computeBookStatus(sub1, sub2);
  if (!s) return '';
  const colors = { ROOM_TO_GROW: '#4caf50', MAXED_OUT: '#e5c900', OVERPLAYED: '#ef5350' };
  return `<span style="font-size:.7rem;padding:.15rem .35rem;border-radius:3px;background:rgba(255,255,255,.06);color:${colors[s] || '#888'}">${s.replace('_',' ')}</span>`;
};

const GAP_COLORS = {
  ANYONES_GAME:   { bg: 'rgba(255,255,255,.06)', text: '#aaa' },
  CLOSE:          { bg: 'rgba(255,200,0,.12)',   text: '#e5c900' },
  NOT_IN_BALLPARK:{ bg: 'rgba(239,83,80,.15)',   text: '#ef5350' },
};

export async function renderResultsTab() {
  return `
    <div class="results-controls" style="display:flex;gap:.75rem;align-items:center;margin-bottom:1rem;flex-wrap:wrap">
      <div style="display:flex;gap:.5rem;align-items:center">
        <label style="font-size:.82rem;color:var(--text-muted)">Competition</label>
        <select id="results-comp-select" class="form-input" style="width:280px"
          onchange="window.loadCompetitionSheet()">
          <option value="">— select competition —</option>
        </select>
      </div>
      <div style="display:flex;gap:.5rem;align-items:center">
        <label style="font-size:.82rem;color:var(--text-muted)">Primary band</label>
        <select id="results-primary-band" class="form-input" style="width:200px"
          onchange="window.renderSheetWithPrimary()">
          <option value="">— none —</option>
        </select>
      </div>
    </div>
    <div id="comp-sheet-container">
      <div class="empty-state">Select a competition to view the competitive sheet</div>
    </div>
  `;
}

export async function loadResultsSelectors() {
  try {
    // Load competition list — most recent first
    const comps = await apiFetch('/api/competition/competitions?limit=200');
    const sel = document.getElementById('results-comp-select');
    if (!sel) return;

    window._compList = comps;
    sel.innerHTML = '<option value="">— select competition —</option>' +
      comps.map(c => `<option value="${c.id}">${c.name} (${c.season} · ${c.circuit || '?'})</option>`).join('');

    // If there's a preselected competition (from band detail click), load it
    if (window._compPreselectedComp) {
      sel.value = window._compPreselectedComp;
      window._compPreselectedComp = null;
      await window.loadCompetitionSheet();
    }
  } catch (err) {
    console.error('[competition-results] failed to load competitions:', err.message);
  }
}

export function initResultsHandlers() {
  window._sheetData = null;

  window.loadCompetitionSheet = async function() {
    const compId = document.getElementById('results-comp-select')?.value;
    if (!compId) return;

    const container = document.getElementById('comp-sheet-container');
    if (container) container.innerHTML = '<div class="loading-state">Loading sheet…</div>';

    try {
      const data = await apiFetch(`/api/competition/competitions/${compId}/sheet`);
      window._sheetData = data;
      populatePrimaryBandSelector(data.entries);
      window.renderSheetWithPrimary();
    } catch (err) {
      if (container) container.innerHTML = `<div class="error-state">Failed to load sheet: ${err.message}</div>`;
    }
  };

  window.renderSheetWithPrimary = function() {
    const data = window._sheetData;
    if (!data) return;
    const primaryBandId = parseInt(document.getElementById('results-primary-band')?.value) || null;
    const container = document.getElementById('comp-sheet-container');
    if (container) container.innerHTML = renderCompetitiveSheet(data, primaryBandId);
  };
}

function populatePrimaryBandSelector(entries) {
  const sel = document.getElementById('results-primary-band');
  if (!sel) return;
  sel.innerHTML = '<option value="">— none —</option>' +
    entries.map(e => `<option value="${e.band_id}">${e.band_name}</option>`).join('');
}

function renderCompetitiveSheet(data, primaryBandId) {
  const { competition: comp, entries } = data;
  if (!entries.length) return '<div class="empty-state">No entries found for this competition.</div>';

  const primaryEntry = primaryBandId ? entries.find(e => e.band_id === primaryBandId) : null;
  const primaryScore = primaryEntry ? parseFloat(primaryEntry.total_score) : null;

  // Collect all unique captions in sort order
  const captionKeys = [];
  const seenCaps = new Set();
  for (const entry of entries) {
    const byCaption = groupCaptionScores(entry.caption_scores);
    for (const key of Object.keys(byCaption)) {
      if (!seenCaps.has(key)) { seenCaps.add(key); captionKeys.push(key); }
    }
  }

  const headerCells = captionKeys.map(k => `<th style="padding:.25rem .4rem;white-space:nowrap;font-size:.75rem">${k}</th>`).join('');

  const rows = entries.map(entry => {
    const isPrimary = entry.band_id === primaryBandId;
    const score = parseFloat(entry.total_score);
    const gap   = primaryScore != null && !isPrimary ? primaryScore - score : null;
    const gapClass = gap != null ? classifyGap(gap) : null;
    const { bg, text } = gapClass ? (GAP_COLORS[gapClass] || {}) : {};

    const byCaption = groupCaptionScores(entry.caption_scores);

    const captionCells = captionKeys.map(key => {
      const capData = byCaption[key];
      if (!capData) return '<td style="padding:.25rem .4rem;font-size:.75rem;color:#555">—</td>';
      return renderCaptionCell(capData);
    }).join('');

    return `
      <tr style="${isPrimary ? 'background:rgba(99,179,237,.08);' : ''}">
        <td style="padding:.3rem .4rem;font-weight:${isPrimary ? '600' : '400'}">${entry.placement ? `#${entry.placement}` : '—'}</td>
        <td style="padding:.3rem .4rem;font-weight:${isPrimary ? '600' : '400'};white-space:nowrap">${entry.band_name}</td>
        <td style="padding:.3rem .4rem;font-variant-numeric:tabular-nums">${entry.total_score ?? '—'}</td>
        <td style="padding:.3rem .4rem">
          ${gap != null
            ? `<span style="font-size:.78rem;padding:.15rem .4rem;border-radius:3px;background:${bg};color:${text}">
                ${gap >= 0 ? '+' : ''}${gap.toFixed(2)} ${gapClass ? '(' + gapClass.replace('_',' ') + ')' : ''}
               </span>`
            : '—'}
        </td>
        ${captionCells}
      </tr>`;
  }).join('');

  return `
    <div style="margin-bottom:.5rem;font-size:.82rem;color:var(--text-muted)">
      <strong>${comp.name}</strong> · ${comp.event_date ? comp.event_date.slice(0,10) : '—'} · ${comp.circuit || '—'} · ${comp.round || 'regular'}
    </div>
    <div style="overflow-x:auto">
      <table class="schedule-table" style="font-size:.82rem;min-width:600px">
        <thead>
          <tr>
            <th style="padding:.25rem .4rem">#</th>
            <th style="padding:.25rem .4rem">Band</th>
            <th style="padding:.25rem .4rem">Total</th>
            <th style="padding:.25rem .4rem">Gap</th>
            ${headerCells}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

// Group caption scores by (raw_name × panel_id) — mirrors byCaption pattern from client-card.js.
// Handles dual-panel OE without data overwrites.
function groupCaptionScores(scores) {
  const byCaption = {};
  for (const s of scores) {
    const key = s.caption_raw_name + (s.panel_id != null ? `|p${s.panel_id}` : '');
    if (!byCaption[key]) byCaption[key] = { rawName: s.caption_raw_name, panelId: s.panel_id, scores: {} };
    byCaption[key].scores[s.score_component] = s.score_value;
    if (s.caption_group) byCaption[key].captionGroup = s.caption_group;
    if (s.judge_name)    byCaption[key].judgeName    = s.judge_name;
    if (s.sub_score_1_label) byCaption[key].sub1Label = s.sub_score_1_label;
    if (s.sub_score_2_label) byCaption[key].sub2Label = s.sub_score_2_label;
    if (s.sort_order)    byCaption[key].sortOrder = s.sort_order;
  }
  // Re-key by display label (caption group if available, else rawName)
  const result = {};
  for (const [, cap] of Object.entries(byCaption).sort((a, b) => (a[1].sortOrder || 999) - (b[1].sortOrder || 999))) {
    const displayKey = cap.captionGroup || cap.rawName;
    const uniqueKey  = cap.panelId != null ? `${displayKey} (${cap.judgeName || 'P' + cap.panelId})` : displayKey;
    result[uniqueKey] = cap;
  }
  return result;
}

function renderCaptionCell(capData) {
  const { scores, sub1Label, sub2Label } = capData;
  const sub1  = scores.sub1  != null ? parseFloat(scores.sub1)  : null;
  const sub2  = scores.sub2  != null ? parseFloat(scores.sub2)  : null;
  const total = scores.total != null ? parseFloat(scores.total) : null;
  const status = computeBookStatus(sub1, sub2);

  const bookBadge = status
    ? { ROOM_TO_GROW: '🟢', MAXED_OUT: '🟡', OVERPLAYED: '🔴' }[status] || ''
    : '';

  const parts = [];
  if (sub1 != null) parts.push(`<span title="${sub1Label || 'sub1'}">${sub1.toFixed(3)}</span>`);
  if (sub2 != null) parts.push(`<span title="${sub2Label || 'sub2'}">${sub2.toFixed(3)}</span>`);
  if (total != null) parts.push(`<strong>${total.toFixed(3)}</strong>`);

  return `<td style="padding:.25rem .4rem;font-size:.75rem;font-variant-numeric:tabular-nums;white-space:nowrap">
    ${parts.join(' / ')}${bookBadge ? ' ' + bookBadge : ''}
  </td>`;
}
